import React, { useState, useEffect, useRef, useCallback } from 'react';
import { fetchCardByName, scryfallFetch } from '../utils/scryfallApi';
import { sanitizeCommanderName } from '../utils/sanitizeName';
import API_URL from '../config';
import '../styles/PartnerSelector.css';

// Scryfall SVG mana symbol URLs
const getManaSymbolUrl = (color) => {
  return `https://svgs.scryfall.io/card-symbols/${color}.svg`;
};

// Partner types and their Scryfall search queries
const getPartnerSearchQuery = (partnerType, commanderName) => {
  switch (partnerType) {
    case 'partner':
      // Generic partner - can pair with any other partner
      return 'keyword:partner -keyword:"partner with" -is:digital';
    case 'partner_with':
      // Extract the specific partner name from oracle text
      return null; // Will be handled specially
    case 'friends_forever':
      return 'oracle:"Friends forever" -is:digital';
    case 'choose_a_background':
      return 'type:background -is:digital';
    case 'background':
      // Background cards pair with "Choose a Background" commanders
      return 'keyword:"choose a background" -is:digital';
    case 'doctors_companion':
      return "oracle:\"Doctor's companion\" -is:digital";
    case 'doctor':
      // Doctor cards pair with Doctor's companion
      return 'type:doctor -is:digital is:commander';
    default:
      return null;
  }
};

// Determine partner type from card data
const getPartnerType = (cardData) => {
  const keywords = cardData.keywords || [];
  const oracleText = cardData.oracle_text || '';
  const typeLine = cardData.type_line || '';

  // Check for "Partner with [Name]" - most specific first
  if (keywords.includes('Partner with') || oracleText.match(/Partner with \w/)) {
    const match = oracleText.match(/Partner with ([^(\n]+)/);
    if (match) {
      return { type: 'partner_with', partnerName: match[1].trim() };
    }
  }

  // Check for Friends forever
  if (oracleText.includes('Friends forever')) {
    return { type: 'friends_forever' };
  }

  // Check for "Choose a Background"
  if (keywords.includes('Choose a background')) {
    return { type: 'choose_a_background' };
  }

  // Check if this IS a Background (pairs with Choose a Background commanders)
  if (typeLine.toLowerCase().includes('background')) {
    return { type: 'background' };
  }

  // Check for Doctor's companion
  if (oracleText.includes("Doctor's companion")) {
    return { type: 'doctors_companion' };
  }

  // Check if this is a Doctor (pairs with Doctor's companion)
  if (typeLine.includes('Doctor') && keywords.includes('Partner')) {
    return { type: 'doctor' };
  }

  // Check for generic Partner (must check last as "Partner with" also includes Partner keyword)
  if (keywords.includes('Partner') && !keywords.includes('Partner with')) {
    return { type: 'partner' };
  }

  return null;
};

// Get display text for partner type
const getPartnerTypeLabel = (partnerType) => {
  switch (partnerType) {
    case 'partner':
      return 'Partner';
    case 'partner_with':
      return 'Specific Partner';
    case 'friends_forever':
      return 'Friends Forever';
    case 'choose_a_background':
      return 'Background';
    case 'background':
      return 'Choose a Background Commander';
    case 'doctors_companion':
      return "Doctor's Companion";
    case 'doctor':
      return "Doctor's Companion";
    default:
      return 'Partner';
  }
};

const PartnerSelector = ({ 
  commanderData, 
  partnerInfo, 
  onPartnerSelect, 
  onNoPartner 
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [suggestedPartners, setSuggestedPartners] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSearching, setIsSearching] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const searchInputRef = useRef(null);
  const debounceRef = useRef(null);

  // Fetch suggested partners on mount
  useEffect(() => {
    const fetchSuggestedPartners = async () => {
      setIsLoading(true);
      
      try {
        // Handle "Partner with [Name]" - only one valid partner
        if (partnerInfo.type === 'partner_with' && partnerInfo.partnerName) {
          const partnerData = await fetchCardByName(partnerInfo.partnerName);
          if (partnerData && !partnerData._error) {
            setSuggestedPartners([{
              name: partnerData.name,
              image: partnerData.image_uris?.art_crop || partnerData.card_faces?.[0]?.image_uris?.art_crop || '',
              colors: partnerData.color_identity || [],
              manaCost: partnerData.mana_cost || partnerData.card_faces?.[0]?.mana_cost || '',
              typeLine: partnerData.type_line || ''
            }]);
          }
          setIsLoading(false);
          return;
        }

        // For generic Partner commanders, try EDHREC's top pairings first
        if (partnerInfo.type === 'partner') {
          const formattedName = sanitizeCommanderName(commanderData.name);
          
          try {
            const response = await fetch(`${API_URL}/v1/commander/${formattedName}/partners`);
            if (response.ok) {
              const topPartners = await response.json();
              console.log('Top partners from EDHREC:', topPartners);
              
              // Fetch full card data for each top partner from Scryfall
              const partnerPromises = topPartners.map(async (partner) => {
                try {
                  const cardData = await fetchCardByName(partner.name);
                  if (cardData && !cardData._error) {
                    return {
                      name: cardData.name,
                      image: cardData.image_uris?.art_crop || cardData.card_faces?.[0]?.image_uris?.art_crop || '',
                      colors: cardData.color_identity || [],
                      manaCost: cardData.mana_cost || cardData.card_faces?.[0]?.mana_cost || '',
                      typeLine: cardData.type_line || '',
                      deckCount: partner.deckCount
                    };
                  }
                } catch (e) {
                  console.error('Error fetching partner card:', partner.name, e);
                }
                return null;
              });
              
              const partners = (await Promise.all(partnerPromises)).filter(p => p !== null);
              if (partners.length > 0) {
                setSuggestedPartners(partners);
                setIsLoading(false);
                return;
              }
            }
          } catch (e) {
            console.log('EDHREC partners not available, falling back to Scryfall');
          }
        }

        // Fallback: For other partner types (or if EDHREC fails), search Scryfall
        const query = getPartnerSearchQuery(partnerInfo.type, commanderData.name);
        if (!query) {
          setIsLoading(false);
          return;
        }

        // Exclude the current commander from results
        const excludeName = commanderData.name.split(' // ')[0]; // Handle DFCs
        const fullQuery = `${query} -!"${excludeName}"`;
        
        const data = await scryfallFetch(
          `https://api.scryfall.com/cards/search?q=${encodeURIComponent(fullQuery)}&order=edhrec&unique=cards`
        );

        if (data.data && !data._error) {
          const partners = data.data.slice(0, 5).map(card => ({
            name: card.name,
            image: card.image_uris?.art_crop || card.card_faces?.[0]?.image_uris?.art_crop || '',
            colors: card.color_identity || [],
            manaCost: card.mana_cost || card.card_faces?.[0]?.mana_cost || '',
            typeLine: card.type_line || ''
          }));
          setSuggestedPartners(partners);
        }
      } catch (error) {
        console.error('Error fetching suggested partners:', error);
      }
      
      setIsLoading(false);
    };

    fetchSuggestedPartners();
  }, [commanderData, partnerInfo]);

  // Search for partners
  const searchPartners = useCallback(async (query) => {
    if (query.length < 3) {
      setSearchResults([]);
      return;
    }

    setIsSearching(true);
    try {
      const baseQuery = getPartnerSearchQuery(partnerInfo.type, commanderData.name);
      if (!baseQuery) {
        setIsSearching(false);
        return;
      }

      const excludeName = commanderData.name.split(' // ')[0];
      const fullQuery = `${baseQuery} name:${query} -!"${excludeName}"`;

      const data = await scryfallFetch(
        `https://api.scryfall.com/cards/search?q=${encodeURIComponent(fullQuery)}&order=edhrec&unique=cards`
      );

      if (data.data && !data._error) {
        const results = data.data.slice(0, 8).map(card => ({
          name: card.name,
          image: card.image_uris?.art_crop || card.card_faces?.[0]?.image_uris?.art_crop || '',
          colors: card.color_identity || [],
          manaCost: card.mana_cost || card.card_faces?.[0]?.mana_cost || '',
          typeLine: card.type_line || ''
        }));
        setSearchResults(results);
      } else {
        setSearchResults([]);
      }
    } catch (error) {
      console.error('Error searching partners:', error);
      setSearchResults([]);
    }
    setIsSearching(false);
  }, [partnerInfo.type, commanderData.name]);

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);

    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = setTimeout(() => {
      searchPartners(value);
    }, 300);
  };

  const handlePartnerClick = async (partner) => {
    // Fetch full card data for the partner
    try {
      const fullData = await fetchCardByName(partner.name);
      onPartnerSelect({
        name: partner.name,
        colors: fullData.color_identity || partner.colors,
        image: partner.image,
        typeLine: fullData.type_line || partner.typeLine
      });
    } catch (error) {
      console.error('Error fetching partner data:', error);
      onPartnerSelect(partner);
    }
  };

  const renderColorIdentity = (colors) => {
    if (!colors || colors.length === 0) {
      return (
        <img 
          src={getManaSymbolUrl('C')} 
          alt="Colorless" 
          className="mana-symbol"
        />
      );
    }
    return colors.map((color) => (
      <img 
        key={color}
        src={getManaSymbolUrl(color)} 
        alt={color} 
        className="mana-symbol"
      />
    ));
  };

  const formatDeckCount = (count) => {
    if (count >= 1000) {
      return `${(count / 1000).toFixed(1)}k decks`;
    }
    return `${count} decks`;
  };

  const renderPartnerCard = (partner, index) => (
    <div 
      key={partner.name} 
      className="partner-card"
      onClick={() => handlePartnerClick(partner)}
      style={{ animationDelay: `${index * 0.1}s` }}
    >
      <div 
        className="partner-card-image"
        style={{ backgroundImage: partner.image ? `url(${partner.image})` : 'none' }}
      >
        {partner.deckCount && (
          <div className="partner-deck-count">{formatDeckCount(partner.deckCount)}</div>
        )}
      </div>
      <div className="partner-card-info">
        <div className="partner-card-name">{partner.name}</div>
        <div className="partner-card-colors">
          {renderColorIdentity(partner.colors)}
        </div>
      </div>
    </div>
  );

  // For "Partner with" - show only the specific partner
  const isSpecificPartner = partnerInfo.type === 'partner_with';

  return (
    <div className="partner-selector-overlay">
      <div className="partner-selector-modal">
        <div className="partner-selector-header">
          <div className="partner-commander-preview">
            <div 
              className="partner-commander-image"
              style={{ backgroundImage: commanderData.image ? `url(${commanderData.image})` : 'none' }}
            />
            <div className="partner-commander-info">
              <div className="partner-commander-name">{commanderData.name}</div>
              <div className="partner-commander-colors">
                {renderColorIdentity(commanderData.colors)}
              </div>
            </div>
          </div>
          <div className="partner-plus">+</div>
          <div className="partner-question">
            <span className="partner-question-mark">?</span>
          </div>
        </div>

        <div className="partner-selector-content">
          <h2 className="partner-selector-title">
            {isSpecificPartner 
              ? `${commanderData.name} partners with:`
              : `Choose a ${getPartnerTypeLabel(partnerInfo.type)}`
            }
          </h2>
          
          {partnerInfo.type !== 'partner_with' && (
            <p className="partner-selector-subtitle">
              Select from popular options or search for a specific {getPartnerTypeLabel(partnerInfo.type).toLowerCase()}
            </p>
          )}

          {isLoading ? (
            <div className="partner-loading">
              <div className="loading-spinner"></div>
              <p>Finding partners...</p>
            </div>
          ) : (
            <>
              {/* Suggested Partners Grid */}
              <div className="suggested-partners">
                {suggestedPartners.map((partner, index) => renderPartnerCard(partner, index))}
              </div>

              {/* Search Section (not for specific partners) */}
              {!isSpecificPartner && (
                <div className="partner-search-section">
                  {!showSearch ? (
                    <button 
                      className="partner-search-toggle"
                      onClick={() => {
                        setShowSearch(true);
                        setTimeout(() => searchInputRef.current?.focus(), 100);
                      }}
                    >
                      <span className="search-icon">🔍</span>
                      Search for more...
                    </button>
                  ) : (
                    <div className="partner-search-container">
                      <input
                        ref={searchInputRef}
                        type="text"
                        className="partner-search-input"
                        placeholder={`Search for a ${getPartnerTypeLabel(partnerInfo.type).toLowerCase()}...`}
                        value={searchQuery}
                        onChange={handleSearchChange}
                      />
                      {isSearching && <div className="search-spinner"></div>}
                    </div>
                  )}

                  {searchResults.length > 0 && (
                    <div className="search-results">
                      {searchResults.map((partner, index) => renderPartnerCard(partner, index))}
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>

        {/* No Partner Button */}
        {!isSpecificPartner && (
          <button className="no-partner-button" onClick={onNoPartner}>
            Continue without a partner
          </button>
        )}
      </div>
    </div>
  );
};

// Export the helper function for use in App.js
export { getPartnerType };
export default PartnerSelector;
